/**
 * SequencePlayer — Continuous playback of assembled segments.
 *
 * Builds a virtual playlist from segment video URLs.
 * Each segment has in_tc/out_tc within its source asset.
 * On segment end, loads next segment and seeks to in_tc.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Film } from 'lucide-react'

interface PlaylistSegment {
  id: number
  assetId: number
  videoUrl: string
  inTc: number
  outTc: number
  duration: number
}

interface SequencePlayerProps {
  segments: {
    id: number
    asset_id: number
    video_url: string | null
    in_tc: number
    out_tc: number
    duration: number
  }[]
  currentSegmentId?: number | null
  onSegmentChange?: (segmentId: number) => void
  /** Where playback currently is. globalTime is the position within the
   *  assembled sequence; assetId and sourceTime say where that lands in the
   *  original file, which is the clock a source-asset timeline is drawn in.
   *  Both are needed: a consumer showing one asset can only place the
   *  playhead while that asset is the one playing. */
  onTimeUpdate?: (pos: {
    globalTime: number
    assetId: number
    sourceTime: number
  }) => void
}

export default function SequencePlayer({
  segments,
  currentSegmentId,
  onSegmentChange,
  onTimeUpdate,
}: SequencePlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null)

  // Stable playlist — only recomputed when segments actually change
  const playlist = useMemo<PlaylistSegment[]>(() =>
    segments
      .filter(s => s.video_url)
      .map(s => ({
        id: s.id,
        assetId: s.asset_id,
        videoUrl: s.video_url!,
        inTc: s.in_tc,
        outTc: s.out_tc,
        duration: s.duration,
      })),
    [segments],
  )

  const totalDuration = useMemo(
    () => playlist.reduce((sum, s) => sum + s.duration, 0),
    [playlist],
  )

  // Cumulative start times — computed synchronously to avoid one-frame lag
  const cumulativeStarts = useMemo(() => {
    const starts: number[] = []
    let acc = 0
    for (const seg of playlist) {
      starts.push(acc)
      acc += seg.duration
    }
    return starts
  }, [playlist])

  const [currentIndex, setCurrentIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [globalTime, setGlobalTime] = useState(0)

  // Refs for values needed in callbacks without stale closures
  const isPlayingRef = useRef(false)
  const seekingRef = useRef(false)
  const loadingRef = useRef(0) // monotonic counter to detect superseded loads
  const currentIndexRef = useRef(0)

  // Keep refs in sync
  useEffect(() => { isPlayingRef.current = isPlaying }, [isPlaying])
  useEffect(() => { currentIndexRef.current = currentIndex }, [currentIndex])

  // Clamp currentIndex when playlist shrinks
  useEffect(() => {
    if (playlist.length > 0 && currentIndex >= playlist.length) {
      setCurrentIndex(playlist.length - 1)
    }
  }, [playlist.length, currentIndex])

  // Helper: seek video to time, deferring if metadata not yet loaded
  const seekVideo = useCallback((video: HTMLVideoElement, time: number) => {
    if (video.readyState >= 1) {
      video.currentTime = time
    } else {
      const onReady = () => {
        video.currentTime = time
        video.removeEventListener('loadedmetadata', onReady)
      }
      video.addEventListener('loadedmetadata', onReady)
    }
  }, [])

  const loadSegment = useCallback((index: number) => {
    if (index < 0 || index >= playlist.length) return
    const video = videoRef.current
    if (!video) return

    const seg = playlist[index]
    const wasPlaying = isPlayingRef.current
    const loadId = ++loadingRef.current

    // Check if we need to change source by comparing normalized URLs
    const currentSrc = video.currentSrc || ''
    const needsSrcChange = !currentSrc.endsWith(new URL(seg.videoUrl, window.location.href).pathname)

    if (needsSrcChange) {
      video.src = seg.videoUrl
      video.load()
    }

    const doSeek = () => {
      // Guard: if a newer load happened, bail
      if (loadingRef.current !== loadId) return
      seekVideo(video, seg.inTc)
      if (wasPlaying) video.play().catch(() => {})
    }

    if (needsSrcChange) {
      const onCanPlay = () => {
        video.removeEventListener('canplay', onCanPlay)
        doSeek()
      }
      video.addEventListener('canplay', onCanPlay)
    } else {
      seekVideo(video, seg.inTc)
      if (wasPlaying) video.play().catch(() => {})
    }

    setCurrentIndex(index)
    onSegmentChange?.(seg.id)
  }, [playlist, onSegmentChange, seekVideo])

  // Handle timeupdate — track global time and detect segment end
  // Throttled: only update state every ~200ms to reduce re-renders
  const lastTimeUpdateRef = useRef(0)
  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current
    if (!video || seekingRef.current) return

    const idx = currentIndexRef.current
    const seg = playlist[idx]
    if (!seg) return

    // Calculate global time
    const segProgress = video.currentTime - seg.inTc
    const gt = (cumulativeStarts[idx] || 0) + Math.max(0, segProgress)

    // Throttle state updates to ~5/sec
    const now = performance.now()
    if (now - lastTimeUpdateRef.current > 200) {
      setGlobalTime(gt)
      // video.currentTime is already source time; the element is playing
      // the source file, seeked to inTc + offset. No conversion needed.
      onTimeUpdate?.({
        globalTime: gt,
        assetId: seg.assetId,
        sourceTime: video.currentTime,
      })
      lastTimeUpdateRef.current = now
    }

    // Check if segment end reached
    if (video.currentTime >= seg.outTc - 0.05) {
      if (idx < playlist.length - 1) {
        loadSegment(idx + 1)
      } else {
        video.pause()
        setIsPlaying(false)
      }
    }
  }, [playlist, cumulativeStarts, onTimeUpdate, loadSegment])

  // When currentSegmentId changes from outside, seek to that segment
  useEffect(() => {
    if (currentSegmentId == null) return
    const idx = playlist.findIndex(s => s.id === currentSegmentId)
    if (idx !== -1 && idx !== currentIndexRef.current) {
      loadSegment(idx)
    }
  }, [currentSegmentId, playlist, loadSegment])

  // Load first segment when playlist first becomes available
  const hasLoadedFirstRef = useRef(false)
  useEffect(() => {
    if (playlist.length > 0 && videoRef.current && !hasLoadedFirstRef.current) {
      hasLoadedFirstRef.current = true
      const video = videoRef.current
      const seg = playlist[0]
      video.src = seg.videoUrl
      seekVideo(video, seg.inTc)
    }
  }, [playlist, seekVideo])

  const togglePlay = () => {
    const video = videoRef.current
    if (!video) return

    if (isPlaying) {
      video.pause()
      setIsPlaying(false)
    } else {
      video.play().catch(() => {})
      setIsPlaying(true)
    }
  }

  const skipPrev = () => {
    if (currentIndex > 0) {
      loadSegment(currentIndex - 1)
    }
  }

  const skipNext = () => {
    if (currentIndex < playlist.length - 1) {
      loadSegment(currentIndex + 1)
    }
  }

  const handleScrub = (e: React.ChangeEvent<HTMLInputElement>) => {
    const targetTime = parseFloat(e.target.value)
    seekingRef.current = true

    // Find which segment this global time falls in
    let cumulative = 0
    for (let i = 0; i < playlist.length; i++) {
      if (targetTime < cumulative + playlist[i].duration || i === playlist.length - 1) {
        const offsetInSeg = targetTime - cumulative
        const seekTo = playlist[i].inTc + offsetInSeg

        if (i !== currentIndexRef.current) {
          const video = videoRef.current
          if (video) {
            video.src = playlist[i].videoUrl
            video.load()
            seekVideo(video, seekTo)
            setCurrentIndex(i)
            onSegmentChange?.(playlist[i].id)
            if (isPlayingRef.current) video.play().catch(() => {})
          }
        } else if (videoRef.current) {
          videoRef.current.currentTime = seekTo
        }

        setGlobalTime(targetTime)
        // Report from the seek too, not only from the playback tick: a scrub
        // while paused moves the playhead and never fires a tick.
        onTimeUpdate?.({
          globalTime: targetTime,
          assetId: playlist[i].assetId,
          sourceTime: seekTo,
        })
        break
      }
      cumulative += playlist[i].duration
    }

    // Clear seeking flag after browser processes the seek
    requestAnimationFrame(() => { seekingRef.current = false })
  }

  const formatTime = (s: number) => {
    if (!isFinite(s) || s < 0) return '0:00'
    const m = Math.floor(s / 60)
    const sec = Math.floor(s % 60)
    return `${m}:${sec.toString().padStart(2, '0')}`
  }

  if (playlist.length === 0) {
    return (
      <div className="aspect-video rounded-xl bg-black/30 border border-white/5 flex items-center justify-center">
        <div className="text-center">
          <Film className="w-10 h-10 text-[var(--rq-text-soft)] mx-auto mb-2" />
          <p className="text-xs text-[var(--rq-text-soft)]">No playable segments</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {/* Video */}
      <div className="relative aspect-video rounded-xl overflow-hidden bg-black">
        <video
          ref={videoRef}
          className="w-full h-full"
          onTimeUpdate={handleTimeUpdate}
          muted={isMuted}
          playsInline
        />
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3">
        <button
          onClick={skipPrev}
          disabled={currentIndex === 0}
          aria-label="Previous segment"
          className="p-1 text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] disabled:opacity-30 transition-colors"
        >
          <SkipBack className="w-4 h-4" />
        </button>

        <button
          onClick={togglePlay}
          aria-label={isPlaying ? 'Pause' : 'Play'}
          className="p-1.5 rounded-full bg-white/10 text-[var(--rq-text)] hover:bg-white/20 transition-colors"
        >
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>

        <button
          onClick={skipNext}
          disabled={currentIndex >= playlist.length - 1}
          aria-label="Next segment"
          className="p-1 text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] disabled:opacity-30 transition-colors"
        >
          <SkipForward className="w-4 h-4" />
        </button>

        <span className="text-[10px] text-[var(--rq-text-soft)] tabular-nums w-20">
          {formatTime(globalTime)} / {formatTime(totalDuration)}
        </span>

        <input
          type="range"
          min={0}
          max={totalDuration}
          step={0.1}
          value={globalTime}
          onChange={handleScrub}
          aria-label="Seek"
          className="flex-1 h-1 accent-[var(--rq-blue)] bg-white/10 rounded-full cursor-pointer"
        />

        <button
          onClick={() => setIsMuted(!isMuted)}
          aria-label={isMuted ? 'Unmute' : 'Mute'}
          className="p-1 text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] transition-colors"
        >
          {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      </div>
    </div>
  )
}
